import { DestroyRef, Directive, ElementRef, inject, output } from '@angular/core';
import { isTapPointer, TAP_SLOP_PX } from './tap';

/**
 * Content that handles a tap itself: controls, and anything marked `data-handles-tap` (a stop date
 * that shows its times).
 */
const OWN_TAP_SELECTOR = 'button, a, input, select, textarea, label, [data-handles-tap]';

/**
 * How long a tap waits before it opens: a second tap within it is the other half of a double
 * tap. Opening at once would put the dialog's backdrop under that second tap and close it again.
 */
const DOUBLE_TAP_WINDOW_MS = 300;

interface TapStart {
  pointerType: string;
  x: number;
  y: number;
}

interface PendingTap {
  owner: OpenItemDirective;
  timer: ReturnType<typeof setTimeout>;
  until: number;
}

/** One for the whole page, so a double tap whose halves land on two rows still opens one. */
let pendingTap: PendingTap | null = null;

function activePendingTap(): PendingTap | null {
  return pendingTap && Date.now() < pendingTap.until ? pendingTap : null;
}

function cancelPendingTap(): void {
  if (pendingTap) {
    clearTimeout(pendingTap.timer);
    pendingTap = null;
  }
}

/**
 * Opens a table row or board card: on double-click, as the mouse always has, and on a single
 * touch or pen tap, since mobile browsers don't reliably turn a double tap into `dblclick`. A tap
 * on a control inside the item is left to that control, and a double tap opens it once.
 *
 * `<fr8bot-ui-table-row … (fr8botOpenItem)="openLoad(row)">`
 */
@Directive({
  selector: '[fr8botOpenItem]',
  host: {
    '(dblclick)': 'openOnDoubleClick()',
    '(pointerdown)': 'startTap($event)',
    '(click)': 'openOnTap($event)',
  },
})
export class OpenItemDirective {
  readonly fr8botOpenItem = output<void>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  /**
   * The press behind the coming `click` — `click` is not a PointerEvent everywhere, and a click
   * with no press of its own (keyboard, `element.click()`) must not count as a tap.
   */
  private tapStart: TapStart | null = null;
  private lastPointerType = '';

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (pendingTap?.owner === this) {
        cancelPendingTap();
      }
    });
  }

  protected startTap(event: PointerEvent): void {
    this.lastPointerType = event.pointerType;
    this.tapStart = { pointerType: event.pointerType, x: event.clientX, y: event.clientY };
  }

  protected openOnDoubleClick(): void {
    // A double tap that the browser also reports as `dblclick` is already opening through its tap.
    if (!isTapPointer(this.lastPointerType)) {
      this.fr8botOpenItem.emit();
    }
  }

  protected openOnTap(event: MouseEvent): void {
    const start = this.tapStart;
    this.tapStart = null;

    if (!start || !isTapPointer(start.pointerType) || this.hasMoved(start, event)) {
      return;
    }

    if (this.isOnOwnTap(event)) {
      // The control takes this tap; an item still waiting to open would cover what it opens.
      cancelPendingTap();
      return;
    }

    if (activePendingTap()) {
      return;
    }

    pendingTap = {
      owner: this,
      timer: setTimeout(() => this.fr8botOpenItem.emit(), DOUBLE_TAP_WINDOW_MS),
      until: Date.now() + DOUBLE_TAP_WINDOW_MS,
    };
  }

  private hasMoved(start: TapStart, event: MouseEvent): boolean {
    return Math.hypot(event.clientX - start.x, event.clientY - start.y) > TAP_SLOP_PX;
  }

  /** Only controls inside the item count; the item itself may sit inside a link or a label. */
  private isOnOwnTap(event: MouseEvent): boolean {
    const control = event.target instanceof Element ? event.target.closest(OWN_TAP_SELECTOR) : null;

    return !!control && control !== this.host && this.host.contains(control);
  }
}
